import type { DialNumberHistoryEntry, DialNumberLimit } from './ChangeDialNumberPopup';
import { cn } from '../cn';
import { CallerNumberLine } from './CallerNumberLine';
import { ChangeDialNumberPopup } from './ChangeDialNumberPopup';

export interface ContactDialNumberPureProps {
  contactName?: string;
  /**
   * The contact's pinned from-number, or null when nothing is pinned yet (no call has been placed
   * to them from this rep, and nobody has picked a number by hand).
   */
  stickyNumber: string | null;
  /** The pin is settled and no longer rotates — see `ChangeDialNumberPopupProps.locked`. */
  rotationLocked?: boolean;
  /**
   * The pin is at today's call limit, so the next call goes out from this number instead
   * (`voiceCall.getContactDialInfo` → `standInNumber`). The caption names it rather than the pin —
   * it's what the prospect will see. Null/absent = the next call uses the pin.
   */
  standInNumber?: string | null;
  /** The rep's assigned numbers (E.164) — the only values the contact can be pinned to. */
  bucketNumbers: string[];
  /** Which numbers this contact has actually been called from, most recently used first. */
  history?: DialNumberHistoryEntry[];
  /** Each number's calls today against its daily limit — see `ChangeDialNumberPopupProps.numberLimits`. */
  numberLimits?: DialNumberLimit[];
  /**
   * Non-empty = the picker is disabled and this says why. The dialer refuses to re-pin during a
   * live call (the leg already went out from the old number); this caption sits on the page BEHIND
   * that dialer, so it has to refuse for the same reason instead of offering a back door.
   */
  changeDisabledReason?: string;
  changeOpen?: boolean;
  saving?: boolean;
  clearing?: boolean;
  onOpenChange: () => void;
  onCloseChange: () => void;
  onConfirmChange: (phoneNumber: string) => void;
  /** Drop the association so calls rotate again. Offered only when there is a pin to clear. */
  onClear: () => void;
  className?: string;
}

/**
 * The contact-detail counterpart to the dialer's "Calling from … Change" line: it shows which of
 * the rep's numbers the NEXT call will go out from, and lets them pin a different one *before*
 * dialing.
 *
 * Why this exists separately from the dialer's copy of the affordance: the dialer only mounts once
 * a call is already in flight, and its Change is deliberately disabled until the call ends — so
 * until now the earliest a rep could influence the association was *after* a call they'd already
 * placed from the wrong number.
 *
 * Renders nothing when the rep has no bucket and nothing pinned — there'd be no number to show and
 * nothing valid to pick (the server only accepts numbers from the rep's own bucket).
 */
export function ContactDialNumberPure({
  contactName,
  stickyNumber,
  rotationLocked,
  standInNumber,
  bucketNumbers,
  history,
  numberLimits,
  changeDisabledReason,
  changeOpen,
  saving,
  clearing,
  onOpenChange,
  onCloseChange,
  onConfirmChange,
  onClear,
  className,
}: ContactDialNumberPureProps) {
  if (stickyNumber == null && bucketNumbers.length === 0) return null;

  // A pin at today's call limit lends the next call to a stand-in — name that, it's what will dial.
  // Nothing pinned yet: the dial picks the rep's least-loaded number, which we can't know here — so
  // the caption names the rotation instead of a number. The exception is a single-number bucket,
  // where rotation has no choice to make and we can name what the next call will actually use.
  const effectiveNumber =
    standInNumber ?? stickyNumber ?? (bucketNumbers.length === 1 ? (bucketNumbers[0] ?? null) : null);
  // Open the picker when there's another number to move to — or when a pin exists at all, because
  // the picker is also the only way to UNSET one, and a settled pin on a single-number bucket would
  // otherwise be permanent. A single-number bucket with nothing pinned still offers nothing: there
  // is no choice to make and nothing to undo.
  const canOpenPicker = bucketNumbers.some((n) => n !== effectiveNumber) || stickyNumber != null;
  // An empty bucket means a legacy / superadmin-provisioned rep dialing from the fallback number,
  // not a pin that has gone stale. A stand-in is always one of the rep's own numbers.
  const stale =
    standInNumber == null && stickyNumber != null && bucketNumbers.length > 0 && !bucketNumbers.includes(stickyNumber);

  return (
    <>
      <CallerNumberLine
        callerNumber={effectiveNumber}
        stale={stale}
        onChangeNumber={canOpenPicker ? onOpenChange : undefined}
        changeDisabledReason={changeDisabledReason}
        // The contact-detail action section this renders in restyles every descendant button
        // (`[&_button]:text-sm`), and that descendant selector outranks the Button's own size class —
        // so without an important override the affordance renders larger than the caption beside it.
        // Pinned by the "inside the action section container" test. The dialer uses CallerNumberLine
        // directly and is unaffected.
        className={cn('[&_button]:text-xs!', className)}
      />

      {changeOpen && (
        <ChangeDialNumberPopup
          contactName={contactName}
          currentNumber={stickyNumber}
          locked={rotationLocked}
          bucketNumbers={bucketNumbers}
          history={history}
          numberLimits={numberLimits}
          saving={saving}
          clearing={clearing}
          onConfirm={onConfirmChange}
          // Nothing pinned = nothing to unset, so the affordance is absent rather than dead.
          onClear={stickyNumber == null ? undefined : onClear}
          onClose={onCloseChange}
        />
      )}
    </>
  );
}
